import { Observable } from "rxjs";
import { finalize, shareReplay } from "rxjs/operators";

/**
 * Colapsa las peticiones idénticas que se solapan en el tiempo: quien pida una
 * clave que ya está **en vuelo** se engancha a esa petición en vez de abrir otra,
 * y todos reciben **el mismo objeto**.
 *
 * Existe porque `ProcesoEtapasPage` monta el contenido de una etapa de actividad
 * única **dos veces** —las ramas móvil y desktop conviven en el DOM y se alternan
 * por CSS—, así que las dos instancias piden en el mismo tick y la etapa pedía
 * cada endpoint por duplicado.
 *
 * **No es un cache**: `finalize` suelta la clave al completar o fallar, así que al
 * volver a entrar a la etapa se pide de nuevo (y un error no la deja pegada).
 * Para cachear de verdad, el patrón del módulo sigue siendo el `BehaviorSubject`.
 *
 * Cada servicio instancia el suyo (no es un `@Injectable`): las claves son del
 * servicio que las emite, no de un mapa global compartido entre etapas.
 */
export class InFlightRequests {
	/** Peticiones en vuelo, por clave. */
	private pending = new Map<string, Observable<unknown>>();

	share<T>(key: string, request: () => Observable<T>): Observable<T> {
		const inFlight = this.pending.get(key) as Observable<T> | undefined;
		if (inFlight) return inFlight;

		const shared = request().pipe(
			finalize(() => this.pending.delete(key)),
			shareReplay({ bufferSize: 1, refCount: false }),
		);
		this.pending.set(key, shared);
		return shared;
	}
}
